import { useEffect, useState } from 'react';
import { AI_STRATEGIES } from '@sheepocalypse/shared';
import { useLobbyStore, useSocketStore } from '../../stores/socketStore';

export function LobbyCreator() {
  const [lobbyName, setLobbyName] = useState('');
  const [maxPlayers, setMaxPlayers] = useState(4);
  const [boardWidth, setBoardWidth] = useState(6);
  const [boardHeight, setBoardHeight] = useState(6);
  const [aiPlayers, setAiPlayers] = useState(0);
  const [spectatorMode, setSpectatorMode] = useState(false);
  const [aiStrategy, setAiStrategy] = useState<(typeof AI_STRATEGIES)[number]>('balanced');
  const [isCreating, setIsCreating] = useState(false);
  const { setCurrentLobby, setPlayerId, draftName, setDraftName, draftColor } = useLobbyStore();
  const { joinLobby } = useSocketStore();

  useEffect(() => {
    const maxAiPlayers = Math.max(0, spectatorMode ? maxPlayers : maxPlayers - 1);
    if (aiPlayers > maxAiPlayers) {
      setAiPlayers(maxAiPlayers);
    }
  }, [aiPlayers, maxPlayers, spectatorMode]);

  useEffect(() => {
    if (spectatorMode) {
      setAiPlayers(maxPlayers);
    }
  }, [spectatorMode, maxPlayers]);

  const handleCreate = async () => {
    if (!draftName.trim()) return;

    setIsCreating(true);

    try {
      const response = await fetch('/api/lobbies', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          hostName: draftName.trim(),
          hostColor: draftColor,
          aiPlayers: Array.from({ length: aiPlayers }).map((_, index) => ({
            name: `AI ${index + 1}`,
            strategy: aiStrategy,
          })),
          spectatorMode,
          name: lobbyName.trim() || undefined,
          maxPlayers,
          rules: {
            board: {
              size: { width: boardWidth, height: boardHeight },
            },
          },
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create lobby');
      }

      const data = await response.json();

      setPlayerId(data.playerId);
      joinLobby(data.lobbyId, data.playerId);

      // Fetch lobby details
      const lobbyResponse = await fetch(`/api/lobbies/${data.lobbyId}`);
      const lobby = await lobbyResponse.json();
      setCurrentLobby(lobby);
    } catch (error) {
      console.error('Error creating lobby:', error);
      alert('Failed to create lobby. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Your Name</label>
        <input
          data-testid="create-host-name-input"
          type="text"
          value={draftName}
          onChange={e => setDraftName(e.target.value)}
          placeholder="Enter your name"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          maxLength={20}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Game Name (optional)</label>
        <input
          data-testid="create-lobby-name-input"
          type="text"
          value={lobbyName}
          onChange={e => setLobbyName(e.target.value)}
          placeholder="My Awesome Game"
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          maxLength={30}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Board Size</label>
        <div className="grid grid-cols-2 gap-2">
          <input
            data-testid="create-board-width-input"
            type="number"
            min={2}
            max={12}
            value={boardWidth}
            onChange={e => setBoardWidth(Number(e.target.value))}
            className="px-4 py-3 border border-gray-300 rounded-lg"
            aria-label="Board width"
          />
          <input
            data-testid="create-board-height-input"
            type="number"
            min={2}
            max={12}
            value={boardHeight}
            onChange={e => setBoardHeight(Number(e.target.value))}
            className="px-4 py-3 border border-gray-300 rounded-lg"
            aria-label="Board height"
          />
        </div>
        <div className="flex gap-2 mt-2">
          <button
            data-testid="create-board-preset-5x6"
            type="button"
            onClick={() => {
              setBoardWidth(5);
              setBoardHeight(6);
            }}
            className="px-3 py-1 text-xs bg-gray-100 rounded"
          >
            5x6
          </button>
          <button
            data-testid="create-board-preset-6x6"
            type="button"
            onClick={() => {
              setBoardWidth(6);
              setBoardHeight(6);
            }}
            className="px-3 py-1 text-xs bg-gray-100 rounded"
          >
            6x6
          </button>
          <button
            data-testid="create-board-preset-7x7"
            type="button"
            onClick={() => {
              setBoardWidth(7);
              setBoardHeight(7);
            }}
            className="px-3 py-1 text-xs bg-gray-100 rounded"
          >
            7x7
          </button>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Max Players</label>
        <select
          data-testid="create-max-players-select"
          value={maxPlayers}
          onChange={e => setMaxPlayers(Number(e.target.value))}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value={2}>2 Players</option>
          <option value={3}>3 Players</option>
          <option value={4}>4 Players</option>
          <option value={5}>5 Players</option>
          <option value={6}>6 Players</option>
          <option value={7}>7 Players</option>
          <option value={8}>8 Players</option>
        </select>
      </div>

      <div>
        <label className="inline-flex items-center gap-2 text-sm text-gray-700 mb-2">
          <input
            data-testid="create-spectator-mode-checkbox"
            type="checkbox"
            checked={spectatorMode}
            onChange={e => setSpectatorMode(e.target.checked)}
          />
          Spectator mode (0 human players)
        </label>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">AI Players</label>
        <select
          data-testid="create-ai-player-count-select"
          value={aiPlayers}
          onChange={e => setAiPlayers(Number(e.target.value))}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          disabled={spectatorMode}
        >
          {Array.from({ length: Math.max(0, spectatorMode ? maxPlayers : maxPlayers - 1) + 1 }).map(
            (_, count) => (
              <option key={count} value={count}>
                {count} AI
              </option>
            )
          )}
        </select>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">AI Strategy</label>
        <select
          data-testid="create-ai-strategy-select"
          value={aiStrategy}
          onChange={e => setAiStrategy(e.target.value as (typeof AI_STRATEGIES)[number])}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          {AI_STRATEGIES.map(strategy => (
            <option key={strategy} value={strategy}>
              {strategy}
            </option>
          ))}
        </select>
      </div>

      <button
        data-testid="create-game-button"
        onClick={handleCreate}
        disabled={
          !draftName.trim() ||
          isCreating ||
          !Number.isInteger(boardWidth) ||
          !Number.isInteger(boardHeight) ||
          boardWidth < 2 ||
          boardWidth > 12 ||
          boardHeight < 2 ||
          boardHeight > 12
        }
        className="w-full py-4 bg-green-500 text-white rounded-lg font-bold text-lg hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {isCreating ? 'Creating...' : 'Create Game'}
      </button>

      {(boardWidth < 2 || boardWidth > 12 || boardHeight < 2 || boardHeight > 12) && (
        <p data-testid="create-board-size-error" className="text-xs text-red-600">
          Width/Height must be whole numbers between 2 and 12.
        </p>
      )}
    </div>
  );
}
